import Swiper from 'swiper';
import { Navigation, Pagination, A11y } from 'swiper/modules';

class Slider {
  constructor() {
    this.sliders = [];
    this.initSliders();
  }

  initSliders() {
    // Initialize regular sliders
    const regularSliders = document.querySelectorAll('.js-slider');
    regularSliders.forEach(slider => {
      this.setupSlider(slider);
    });

    // Initialize product sliders
    const productSliders = document.querySelectorAll('.js-slider--product');
    productSliders.forEach(slider => {
      this.setupProductSlider(slider);
    });
  }

  createStatusMessageRegion() {
    // Create status message region compliant with WCAG 2.2 SC 4.1.3
    const statusRegion = document.createElement('div');
    statusRegion.setAttribute('role', 'status');
    statusRegion.setAttribute('aria-live', 'polite');
    statusRegion.setAttribute('aria-atomic', 'true');
    statusRegion.className = 'sr-only';
    statusRegion.style.cssText = 'position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;';
    return statusRegion;
  }

  announceStatusMessage(swiper, statusRegion) {
    const currentSlide = swiper.realIndex + 1;
    const totalSlides = swiper.slides.length;

    // Get slide content - prioritize aria-label, then alt text, then text content
    const currentSlideElement = swiper.slides[swiper.realIndex];
    const slideLabel = currentSlideElement.getAttribute('aria-label') ||
                      currentSlideElement.querySelector('img')?.getAttribute('alt') ||
                      currentSlideElement.textContent.trim();

    const statusMessage = `Slide ${currentSlide} of ${totalSlides}`;
    statusRegion.textContent = statusMessage;
  }

  setupSlider(element) {
    // Create status message region for WCAG 2.2 compliance
    const statusRegion = this.createStatusMessageRegion();
    element.appendChild(statusRegion);

    const slider = new Swiper(element, {
      modules: [Navigation, Pagination, A11y],
      slidesPerView: 1,
      loop: true,
      autoplay: {
        delay: 1000,
        disableOnInteraction: false,
      },
      pagination: {
        el: '.slider-pager',
        clickable: true,
        bulletElement: 'span',
        bulletClass: 'slider-pager-bullet',
        bulletActiveClass: 'is-active',
        enabled: true
      },
      a11y: true,
      on: {
        init: (swiper) => {
          // Add click handler to slides
          swiper.slides.forEach(slide => {
            slide.addEventListener('click', () => {
              swiper.slideNext();
            });
          });

          // Announce initial slide status
          this.announceStatusMessage(swiper, statusRegion);
        },
        slideChange: (swiper) => {
          // Announce slide change status (WCAG 2.2 SC 4.1.3)
          this.announceStatusMessage(swiper, statusRegion);
        }
      }
    });

    this.sliders.push(slider);
    return slider;
  }

  setupProductSlider(element) {
    // Create status message region for WCAG 2.2 compliance
    const statusRegion = this.createStatusMessageRegion();
    element.appendChild(statusRegion);

    const slider = new Swiper(element, {
      modules: [Navigation, Pagination, A11y],
      slidesPerView: 1,
      loop: true,
      pagination: {
        el: '.slider-pager',
        clickable: true,
        bulletElement: 'span',
        bulletClass: 'slider-pager-bullet',
        bulletActiveClass: 'is-active',
        enabled: true
      },
      a11y: true,
      autoHeight: true,
      on: {
        init: (swiper) => {
          // Add click handler to slides
          swiper.slides.forEach(slide => {
            slide.addEventListener('click', () => {
              swiper.slideNext();
            });
          });

          // Announce initial slide status
          this.announceStatusMessage(swiper, statusRegion);
        },
        slideChange: (swiper) => {
          // Announce slide change status (WCAG 2.2 SC 4.1.3)
          this.announceStatusMessage(swiper, statusRegion);
        }
      }
    });

    // Handle variant changes
    document.addEventListener('goToSlide', (event) => {
      const { variantId } = event.detail;
      const slides = element.querySelectorAll('.slider-item');

      // Find slide with matching variant ID
      const targetSlide = [...slides].findIndex(slide => {
        const variantIds = slide.dataset.variantIds;
        return variantIds && variantIds.split(',').includes(String(variantId));
      });

      if (targetSlide >= 0) {
        slider.slideTo(targetSlide);
      }
    });

    this.sliders.push(slider);
    return slider;
  }

  static init() {
    return new Slider();
  }
}

export default Slider;
